import { List, fromJS, is } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';
import * as EnrollmentFilterOperators from 'customer-data-filters/converters/objectSeg/ObjectSegOperatorTypes';
import { TIME_POINT_UI_ALLOWED_LIST_OPERATORS, isInTimePointOperatorList } from 'automation-ui-enrollment/utils/timePointFilterUtils';
export const PERSISTED_REENROLLMENT_LIST_OPERATORS = List([EnrollmentFilterOperators.IS_LESS_THAN_X_DAYS_AGO, EnrollmentFilterOperators.IS_LESS_THAN_X_DAYS_FROM_NOW, EnrollmentFilterOperators.IS_MORE_THAN_X_DAYS_AGO, EnrollmentFilterOperators.IS_MORE_THAN_X_DAYS_FROM_NOW, EnrollmentFilterOperators.UPDATED_IN_LAST_X_DAYS, EnrollmentFilterOperators.NOT_UPDATED_IN_LAST_X_DAYS]);
export function hasFilters(filterBranch) {
  return !!(filterBranch && getAllInnerFilters(filterBranch, {
    blockMap: {
      filterTypes: [],
      operators: []
    }
  }).length);
}
export function checkFilterEquality(filterA_, filterB_) {
  const filterA = fromJS(filterA_)
  /* filterId should no longer exist on the object but I don't want to risk a behavior change.
  only used for associatedLists, which should be deprecated in favor of enrollmentCriteria soon */.delete('filterId').deleteIn(['operation', 'operationType']);
  const filterB = fromJS(filterB_).delete('filterId').deleteIn(['operation', 'operationType']);
  return is(filterA, filterB);
}
export function isPersistedReenrollmentListOperator(filter) {
  return PERSISTED_REENROLLMENT_LIST_OPERATORS.includes(getIn(['operation', 'operator'], filter)) || isInTimePointOperatorList(filter, TIME_POINT_UI_ALLOWED_LIST_OPERATORS);
}
export const getAssociatedFilterBranches = rootFilterBranch => {
  const associatedFilterBranches = [];
  const filterBranchesQueue = [rootFilterBranch];
  let queueIndex = 0;
  while (queueIndex < filterBranchesQueue.length) {
    const filterBranch = filterBranchesQueue[queueIndex++];
    if (filterBranch.filterBranchType === 'ASSOCIATION') {
      /* Associated object filters only declare that they're type `ASSOCIATION` in the parent filter
      could just use `filterBranch` but this mimics the shape of the input filter,
      making it easier to test */
      associatedFilterBranches.push({
        filterBranchOperator: 'AND',
        filterBranchType: 'AND',
        filters: [],
        filterBranches: [filterBranch]
      });
    } else if (filterBranch.filterBranches.length) {
      filterBranchesQueue.push(...filterBranch.filterBranches);
    }
  }
  return associatedFilterBranches;
};
export const isFilterTypeValid = (filterBranch, {
  allowList,
  blockList = []
}) => {
  const isBlocked = blockList.includes(filterBranch.filterBranchType) || blockList.includes(filterBranch.filterType);
  const isAllowed = !allowList ||
  // either filterType or filterBranchType is undefined
  allowList.includes(filterBranch.filterBranchType) || allowList.includes(filterBranch.filterType);
  return !isBlocked && isAllowed;
};
export const isOperatorValid = (filter, {
  allowList,
  absoluteAllowList,
  blockList = []
}) => {
  const operator = filter.operation ? filter.operation.operator : filter.operator;
  return !blockList.includes(operator) && (!allowList || allowList.includes(operator)) || !!(absoluteAllowList && absoluteAllowList.includes(operator));
};
/**
 * Use bfs to get all inner filters from graph of filterBranches
 * @param {filterBranch object} rootFilterBranch
 * @param {{ filterTypes: Array, operators: Array} } blockList
 * @returns {Array[filters object]} filters
 */
export function getAllInnerFilters(rootFilterBranch, {
  timePointOperatorOverrideAllowList = [],
  timePointOperatorOverrideBlockList = [],
  allowMap = {
    filterTypes: null,
    operators: null
  },
  blockMap = {
    filterTypes: List(),
    operators: List()
  },
  absoluteBlockList = {},
  absoluteAllowList = {}
}) {
  if (!rootFilterBranch) {
    return [];
  }
  const filterBranchesQueue = [rootFilterBranch];
  const filters = [];
  let queueIndex = 0;
  while (queueIndex < filterBranchesQueue.length) {
    const filterBranch = filterBranchesQueue[queueIndex++];
    if (isFilterTypeValid(filterBranch, {
      allowList: allowMap.filterTypes,
      blockList: blockMap.filterTypes
    })) {
      if (filterBranch.filterBranches.length) {
        filterBranchesQueue.push(...filterBranch.filterBranches);
      }
      if (filterBranch.filters.length) {
        filters.push(...filterBranch.filters);
      }
    }
  }
  return filters.filter(filter => {
    if (isInTimePointOperatorList(filter, timePointOperatorOverrideAllowList)) {
      return true;
    }
    if (isInTimePointOperatorList(filter, timePointOperatorOverrideBlockList)) {
      return false;
    }
    let blockedOperators = blockMap.operators;
    const absoluteBlockListOperator = absoluteBlockList[filter.property];
    if (absoluteBlockListOperator) {
      /* don't add `absoluteBlockListOperator` if it's undefined because having
      `absoluteBlockListOperator === [undefined]` will block valid classic enrollment
      filters without operators from being returned, since technically `operator === undefined` */
      blockedOperators = blockedOperators.concat(absoluteBlockListOperator);
    }
    const absoluteAllowListOperators = absoluteAllowList[filter.property];
    return isOperatorValid(filter, {
      allowList: allowMap.operators,
      absoluteAllowList: absoluteAllowListOperators,
      blockList: blockedOperators
    });
  });
}